import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import styled, { keyframes } from 'styled-components';

const scaleIn = keyframes`
  from {
    transform: scale(0.8);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

const ChartContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 200px;
  animation: ${scaleIn} 0.8s ease-out;
`;

const ChartWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 60%;
`;

const CenterContent = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 10;
`;

const RiskScore = styled.div`
  font-size: 2rem;
  font-weight: 800;
  background: ${(props) => (props.theme === 'dark'
    ? 'linear-gradient(135deg, #f1f5f9 0%, #cbd5e1 100%)'
    : 'linear-gradient(135deg, #1e293b 0%, #475569 100%)')};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 0.25rem;
`;

const RiskLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(props) => (props.theme === 'dark' ? '#94a3b8' : '#64748b')};
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${(props) => (props.theme === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)')};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  background: ${(props) => (props.theme === 'dark'
    ? 'rgba(30, 41, 59, 0.4)'
    : 'rgba(248, 250, 252, 0.8)')};
  border: 1px solid ${(props) => (props.theme === 'dark'
    ? 'rgba(255, 255, 255, 0.1)'
    : 'rgba(0, 0, 0, 0.1)')};
  min-width: fit-content;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${(props) => props.color};
  flex-shrink: 0;
`;

const LegendText = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const LegendName = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(props) => (props.theme === 'dark' ? '#f1f5f9' : '#1e293b')};
`;

const LegendValue = styled.span`
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(props) => (props.theme === 'dark' ? '#ffffff' : '#0f172a')};
`;

const CustomTooltip = ({ active, payload, theme }) => {
  if (!active || !payload || !payload.length) return null;

  const { name, value, color } = payload[0].payload;

  return (
    <div
      style={{
        backgroundColor: theme === 'dark' ? '#0f172a' : '#ffffff',
        border: `1px solid ${theme === 'dark' ? '#334155' : '#e2e8f0'}`,
        borderRadius: '8px',
        padding: '10px 12px',
        boxShadow: theme === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.4)'
          : '0 8px 32px rgba(0, 0, 0, 0.12)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <div
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '2px',
            backgroundColor: color,
          }}
        />
        <span
          style={{
            fontSize: '11px',
            fontWeight: '600',
            color: theme === 'dark' ? '#f1f5f9' : '#1e293b',
          }}
        >
          {name}
        </span>
      </div>
      <div
        style={{
          fontSize: '14px',
          fontWeight: '800',
          color: theme === 'dark' ? '#ffffff' : '#0f172a',
          marginLeft: '18px',
        }}
      >
        {value}%
      </div>
    </div>
  );
};

// Original export name that matches your import
export const RiskDonutChart = ({ theme = 'light' }) => {
  const data = [
    { name: 'Financial', value: 35, color: '#ef4444' },
    { name: 'Data Exposure', value: 40, color: '#3b82f6' },
    { name: 'Compliance', value: 25, color: '#f59e0b' },
  ];

  const totalValue = 100;

  return (
    <ChartContainer>
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
              animationBegin={0}
              animationDuration={1200}
              animationEasing="ease-out"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke={theme === 'dark' ? '#0f172a' : '#ffffff'}
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip theme={theme} />} />
          </PieChart>
        </ResponsiveContainer>

        <CenterContent>
          <RiskScore theme={theme}>{totalValue}</RiskScore>
          <RiskLabel theme={theme}>RISK SCORE</RiskLabel>
        </CenterContent>
      </ChartWrapper>

      <LegendContainer theme={theme}>
        {data.map((entry) => (
          <LegendItem key={entry.name} theme={theme}>
            <LegendColor color={entry.color} />
            <LegendText>
              <LegendName theme={theme}>{entry.name}</LegendName>
              <LegendValue theme={theme}>{entry.value}%</LegendValue>
            </LegendText>
          </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};

// Original export name that matches your import
export const SecurityAssessmentChart = ({ theme = 'light' }) => {
  const data = [
    { name: 'High Risk', value: 30, color: '#dc2626' },
    { name: 'Medium Risk', value: 45, color: '#f59e0b' },
    { name: 'Low Risk', value: 25, color: '#10b981' },
  ];

  const totalValue = data.reduce((sum, item) => sum + item.value, 0);

  return (
    <ChartContainer>
      <ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={60}
              paddingAngle={2}
              dataKey="value"
              animationBegin={200}
              animationDuration={1200}
              animationEasing="ease-out"
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  stroke={theme === 'dark' ? '#0f172a' : '#ffffff'}
                  strokeWidth={2}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip theme={theme} />} />
          </PieChart>
        </ResponsiveContainer>

        <CenterContent>
          <RiskScore theme={theme}>{totalValue}</RiskScore>
          <RiskLabel theme={theme}>Assets</RiskLabel>
        </CenterContent>
      </ChartWrapper>

      <LegendContainer theme={theme}>
        {data.map((entry) => (
          <LegendItem key={entry.name} theme={theme}>
            <LegendColor color={entry.color} />
            <LegendText>
              <LegendName theme={theme}>{entry.name}</LegendName>
              <LegendValue theme={theme}>{entry.value}%</LegendValue>
            </LegendText>
          </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};